import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { Label } from 'src/@/components/ui/label';
import { Input } from 'src/@/components/ui/input';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import IdentificacaoSection from './realstate/IdentificacaoSection';
import PoliticasSection from './realstate/PoliticasSection';
import LocacaoVendaSection from './realstate/LocacaoVendaSection';
import FuncoesSection from './realstate/FuncoesSection';
import EscopoSection from './realstate/EscopoSection';
import PerguntasEssenciaisSection from './realstate/PerguntasEssenciaisSection';
import BuscaCatalogoSection from './realstate/BuscaCatalogoSection';
import EnvioCrmParceiroSection from './realstate/EnvioCrmParceiroSection';
import EnvioAtendimentoSection from './realstate/EnvioAtendimentoSection';
import CriacaoLeadFunilSection from './realstate/CriacaoLeadFunilSection';
import TomConversaSection from './realstate/TomConversaSection';
import TransferenciaSection from './realstate/TransferenciaSection';
import InstrucoesAdicionaisSection from './realstate/InstrucoesAdicionaisSection';

export default function RealstateModuleConfig({ aiAgent, changeModuleConfig, changeTomConversa, changePersona }) {
    const { t } = useTranslation('settings');

    // SUB_TABS não é exportado e só é usado neste componente, então fica
    // dentro do function body (precisa de `t`, recalculado a cada render —
    // custo desprezível para 11 itens estáticos).
    const SUB_TABS = [
        { value: 'identificacao', label: t('aiAgents.realstate.moduleConfig.tabIdentificacao', 'Identificação'), icon: 'mdi:office-building-outline' },
        { value: 'politicas', label: t('aiAgents.realstate.moduleConfig.tabPoliticas', 'Políticas'), icon: 'mdi:file-document-outline' },
        { value: 'locacao-venda', label: t('aiAgents.realstate.moduleConfig.tabLocacaoVenda', 'Locação e Venda'), icon: 'mdi:key-variant' },
        { value: 'funcoes', label: t('aiAgents.realstate.moduleConfig.tabFuncoes', 'Funções'), icon: 'mdi:cog-outline' },
        { value: 'escopo', label: t('aiAgents.realstate.moduleConfig.tabEscopo', 'Escopo de Atendimento'), icon: 'mdi:filter-variant' },
        { value: 'busca-catalogo', label: t('aiAgents.realstate.moduleConfig.tabBuscaCatalogo', 'Busca & Catálogo'), icon: 'mdi:magnify' },
        { value: 'tom-conversa', label: t('aiAgents.realstate.moduleConfig.tabTomConversa', 'Tom da Conversa'), icon: 'mdi:chat-processing-outline' },
        { value: 'transferencia', label: t('aiAgents.realstate.moduleConfig.tabTransferencia', 'Transferência'), icon: 'mdi:account-arrow-right-outline' },
        { value: 'perguntas-essenciais', label: t('aiAgents.realstate.moduleConfig.tabPerguntasEssenciais', 'Perguntas Essenciais'), icon: 'mdi:comment-question-outline' },
        { value: 'envio-crm-parceiro', label: t('aiAgents.realstate.moduleConfig.tabIntegracaoCrm', 'Integração CRM'), icon: 'mdi:database-export-outline' },
        { value: 'instrucoes-adicionais', label: t('aiAgents.realstate.moduleConfig.tabInstrucoesAdicionais', 'Instruções Adicionais'), icon: 'mdi:format-quote-close' },
    ];

    return (
        <Tabs defaultValue="identificacao" orientation="vertical" className="flex gap-6 items-start">
            <TabsList className="flex flex-col items-stretch h-auto w-[240px] bg-white rounded-xl border border-gray-100 p-2 gap-1 shrink-0">
                {SUB_TABS.map((tab) => (
                    <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="justify-start gap-2 px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50 data-[state=active]:bg-violet-50 data-[state=active]:text-[#7C3AED] data-[state=active]:shadow-none data-[state=active]:font-semibold w-full"
                    >
                        <Iconify icon={tab.icon} width={16} />
                        <span className="text-left flex-1 truncate">{tab.label}</span>
                    </TabsTrigger>
                ))}
            </TabsList>

            <div className="flex-1 min-w-0">
                <TabsContent value="identificacao" className="mt-0">
                    <IdentificacaoSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} changePersona={changePersona} />
                </TabsContent>
                <TabsContent value="politicas" className="mt-0">
                    <PoliticasSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="locacao-venda" className="mt-0">
                    <LocacaoVendaSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="funcoes" className="mt-0">
                    <FuncoesSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="escopo" className="mt-0">
                    <EscopoSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="busca-catalogo" className="mt-0">
                    <BuscaCatalogoSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="tom-conversa" className="mt-0">
                    <TomConversaSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} changeTomConversa={changeTomConversa} />
                </TabsContent>
                <TabsContent value="transferencia" className="mt-0">
                    <TransferenciaSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="perguntas-essenciais" className="mt-0">
                    <PerguntasEssenciaisSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
                <TabsContent value="envio-crm-parceiro" className="mt-0">
                    <div className="flex flex-col gap-4">
                        <CriacaoLeadFunilSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                        <IntegracaoParceiroSwitcher aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                    </div>
                </TabsContent>
                <TabsContent value="instrucoes-adicionais" className="mt-0">
                    <InstrucoesAdicionaisSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
                </TabsContent>
            </div>
        </Tabs>
    );
}

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Único parceiro suportado hoje — mesma lista usada nas duas seções (lead e
// atendimento), agora definida uma única vez aqui.
const PARCEIROS = [
    {
        value: 'imoview',
        label: 'Imoview',
        descricao: 'CRM da Universal Software',
        ondeObterToken: 'No Imoview, abra o menu superior direito → "Detalhes do convênio" → copie a "Chave de API".',
    },
];

// Seletor das duas formas de integração com o CRM parceiro. São mutuamente
// exclusivas: o usuário escolhe Envio de Lead OU Envio de Atendimento, e apenas
// a seção correspondente é renderizada. Parceiro + chave são configurados UMA
// vez aqui (antes da escolha do modo) e replicados para os dois blocos de
// config (envioCrmParceiro/envioAtendimento) — as duas formas usam o mesmo
// convênio, não faz sentido pedir a chave duas vezes.
function IntegracaoParceiroSwitcher({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const [revelarToken, setRevelarToken] = useState(false);

    const MODOS_INTEGRACAO = [
        {
            value: 'lead',
            label: t('aiAgents.realstate.moduleConfig.modoLeadLabel', 'Envio de Lead'),
            descricao: t('aiAgents.realstate.moduleConfig.modoLeadDesc', 'Cria um lead no CRM parceiro quando o cliente é qualificado.'),
            icon: 'mdi:database-export-outline',
        },
        {
            value: 'atendimento',
            label: t('aiAgents.realstate.moduleConfig.modoAtendimentoLabel', 'Envio de Atendimento'),
            descricao: t('aiAgents.realstate.moduleConfig.modoAtendimentoDesc', 'Abre um atendimento/oportunidade e guarda o código para uso futuro.'),
            icon: 'mdi:account-tie-outline',
        },
    ];

    const funcoes = aiAgent?.config?.module?.realstate?.funcoes || {};
    const modo = funcoes.integracaoParceiroModo === 'atendimento' ? 'atendimento' : 'lead';

    const setModo = (value) => {
        if (value === modo) return;
        changeModuleConfig({
            funcoes: { ...funcoes, integracaoParceiroModo: value },
        });
    };

    // Fonte de verdade visual: envioCrmParceiro, com fallback pro que já
    // estiver salvo em envioAtendimento (agentes configurados antes dessa
    // mudança podem ter só um dos dois preenchido).
    const crmCfg = funcoes.envioCrmParceiro || {};
    const atendCfg = funcoes.envioAtendimento || {};
    const parceiro = crmCfg.parceiro || atendCfg.parceiro || '';
    const token = crmCfg.token || atendCfg.token || '';
    const parceiroSelecionado = PARCEIROS.find((p) => p.value === parceiro);

    // Escreve nos dois blocos ao mesmo tempo — cada modo continua lendo do
    // próprio cfg.parceiro/cfg.token (nenhuma mudança no backend), só a UI
    // deixa de pedir os mesmos dois campos duas vezes.
    const updateParceiroChave = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                envioCrmParceiro: { ...crmCfg, ...patch },
                envioAtendimento: { ...atendCfg, ...patch },
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('aiAgents.realstate.moduleConfig.partnerLabel', 'Parceiro')}
                </Label>
                <div className="flex flex-col gap-2 max-w-md mt-2">
                    <Select
                        value={parceiro}
                        onValueChange={(value) => updateParceiroChave({ parceiro: value })}
                    >
                        <SelectTrigger className={INPUT_CLS}>
                            <SelectValue placeholder={t('aiAgents.realstate.moduleConfig.selectPartnerPlaceholder', 'Selecione um parceiro...')} />
                        </SelectTrigger>
                        <SelectContent>
                            {PARCEIROS.map((p) => (
                                <SelectItem key={p.value} value={p.value}>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">{p.label}</span>
                                        <span className="text-xs text-gray-500">{p.descricao}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {parceiroSelecionado && (
                    <div className="flex flex-col gap-3 mt-4">
                        <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                            <AlertDescription className="text-xs">
                                {parceiroSelecionado.ondeObterToken}
                            </AlertDescription>
                        </Alert>
                        <div className="flex flex-col gap-2 max-w-md">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t('aiAgents.realstate.moduleConfig.tokenLabel', 'Chave de API (convênio)')}
                            </Label>
                            <div className="relative">
                                <Input
                                    type={revelarToken ? 'text' : 'password'}
                                    placeholder={t('aiAgents.realstate.moduleConfig.tokenPlaceholder', 'Cole aqui a chave do convênio')}
                                    value={token}
                                    onChange={(e) => updateParceiroChave({ token: e.target.value })}
                                    className={`${INPUT_CLS} pr-10`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setRevelarToken((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                    tabIndex={-1}
                                >
                                    <Iconify icon={revelarToken ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('aiAgents.realstate.moduleConfig.integrationFormLabel', 'Forma de integração com o CRM parceiro')}
                </Label>
                <p className="text-xs text-gray-500 mt-1 mb-3">
                    {t('aiAgents.realstate.moduleConfig.integrationFormHint', 'Escolha uma das formas. As duas são mutuamente exclusivas.')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {MODOS_INTEGRACAO.map((m) => {
                        const ativo = modo === m.value;
                        return (
                            <button
                                key={m.value}
                                type="button"
                                onClick={() => setModo(m.value)}
                                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                                    ativo ? 'border-[#7C3AED] bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                                    ativo ? 'bg-[#7C3AED]' : 'bg-violet-50'
                                }`}>
                                    <Iconify icon={m.icon} width={18} className={ativo ? 'text-white' : 'text-violet-600'} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">{m.label}</span>
                                    <span className="text-xs text-gray-500">{m.descricao}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {modo === 'lead' ? (
                <EnvioCrmParceiroSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
            ) : (
                <EnvioAtendimentoSection aiAgent={aiAgent} changeModuleConfig={changeModuleConfig} />
            )}
        </div>
    );
}
